/**
 * Generic List View Queries
 *
 * React Query hooks for displaying records of any object type in list views.
 * Works with both configured list views and auto-generated default list views.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useFieldsByObject } from './fieldQueries';
import { useObject, objectKeys } from './objectQueries';
import { convertFieldToListViewColumn } from '../../hooks/useObjectManager';
import {
  getRecordsForObject,
  getRecordsForObjectPage,
  createRecord,
  updateRecord,
  deleteRecord,
  type GenericRecord
} from '../recordStorageService';
import { keyToObjectId } from '../gobodo/gobodoObjectService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import { listAllStripeInvoices } from '../stripeInvoiceService';
import type { ListView, ListViewRecord, ListViewData, ListViewColumn } from '../../types/listView';

// ============================================
// QUERY KEYS
// ============================================

export const genericListViewKeys = {
  all: ['genericListViews'] as const,
  byObject: (objectApiName: string) => [...genericListViewKeys.all, 'object', objectApiName] as const,
  data: (objectApiName: string, listViewId?: string, page?: number, pageSize?: number) => 
    [...genericListViewKeys.all, 'data', objectApiName, listViewId || 'default', page, pageSize] as const,
};

const DEFAULT_PAGE_SIZE = 25;

function normObjectKey(name: string): string {
  return (name || '').toLowerCase().replace(/__c$|__stg_c$|__stg3_c$/i, '');
}

function getFallbackColumnsForSparseSchema(objectApiName: string): ListViewColumn[] | null {
  const norm = normObjectKey(objectApiName);
  if (norm === 'csaudit') {
    return [
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Phase_Status__c', fieldLabel: 'Phase Status', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'Audit_Type__c', fieldLabel: 'Audit Type', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', isVisible: true, order: 4 },
    ];
  }
  if (norm === 'pawrecord') {
    return [
      { fieldApiName: 'label', fieldLabel: 'Label', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'CSAuditRelField__c', fieldLabel: 'CS Audit', fieldType: 'lookup', isVisible: true, order: 1 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', isVisible: true, order: 2 },
    ];
  }
  if (norm === 'client' || norm === 'cybersweepclient') {
    return [
      { fieldApiName: 'Name__c', fieldLabel: 'Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'firstName', fieldLabel: 'First Name', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'lastName', fieldLabel: 'Last Name', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'personalEmailAddress', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 3 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', isVisible: true, order: 4 },
    ];
  }
  return null;
}

// ============================================
// DEFAULT LIST VIEW GENERATION
// ============================================

/**
 * Generate a default list view from object fields
 */
function generateDefaultListView(
  objectApiName: string,
  objectLabel: string,
  fields: any[]
): ListView {
  const activeFields = fields.filter((f: any) => f.isActive);
  const useFallback = activeFields.length < 3 && getFallbackColumnsForSparseSchema(objectApiName);

  let columns: ListViewColumn[];
  if (useFallback) {
    columns = getFallbackColumnsForSparseSchema(objectApiName)!;
  } else {
    columns = activeFields
      .slice(0, 10)
      .map((field: any, index: number) => convertFieldToListViewColumn(field, index));
  }

  const hasNameColumn = columns.some(col =>
    ['name', 'title', 'subject', 'label', 'clientname', 'clientname__c', 'name__c'].includes(col.fieldApiName.toLowerCase())
  );

  const standardColumns: ListViewColumn[] = [];
  if (!hasNameColumn && columns.length > 0 && !useFallback) {
    const firstTextField = fields.find((f: any) =>
      f.isActive && ['text', 'textarea'].includes(f.fieldType)
    );
    if (firstTextField) {
      standardColumns.push({
        fieldApiName: firstTextField.fieldName,
        fieldLabel: firstTextField.fieldLabel,
        fieldType: 'text',
        isVisible: true,
        order: -1
      });
    }
  }

  if (!columns.some(c => c.fieldApiName === 'createdDate')) {
    standardColumns.push({
      fieldApiName: 'createdDate',
      fieldLabel: 'Created Date',
      fieldType: 'datetime',
      isVisible: true,
      order: 1000
    });
  }

  const allColumns = [...standardColumns, ...columns]
    .sort((a, b) => a.order - b.order)
    .map((col, index) => ({ ...col, order: index }));

  return {
    id: `default_${objectApiName}`,
    name: `All ${objectLabel}`,
    apiName: `default_${objectApiName}`,
    objectApiName: objectApiName,
    description: `Default list view for ${objectLabel}`,
    isDefault: true,
    columns: allColumns,
    filters: [],
    sortBy: {
      fieldApiName: 'createdDate',
      direction: 'desc'
    },
    createdBy: 'System',
    createdDate: new Date().toISOString()
  };
}

/**
 * Convert generic records to ListViewRecord format
 */
function convertToListViewRecords(records: GenericRecord[]): ListViewRecord[] {
  return records.map(record => ({
    ...record,
    // Ensure standard fields are present
    id: record.id,
    createdDate: record.createdDate || record.created_date,
    lastModified: record.lastModified || record.last_modified || record.createdDate
  }));
}

// ============================================
// HOOKS
// ============================================

/**
 * Get available list views for an object (or default if none configured)
 */
export function useGenericListViews(objectApiName: string, objectId: string) {
  const { data: object } = useObject(objectId);
  const { data: fields = [] } = useFieldsByObject(objectId);
  
  return useQuery({
    queryKey: genericListViewKeys.byObject(objectApiName),
    queryFn: async () => {
      // For now, always return default list view
      // In the future, could check for configured list views from Object Manager
      const defaultView = generateDefaultListView(
        objectApiName,
        object?.label || objectApiName,
        fields
      );
      
      return [defaultView];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!objectApiName && !!objectId && !!object && !!fields
  });
}

/**
 * Get list view data (records + list view config)
 * If listViewId is not provided, generates a default list view.
 * Pagination is skipped for Invoice object when using Gobodo (listAllStripeInvoices).
 */
export function useGenericListViewData(
  objectApiName: string,
  objectId: string,
  listViewId?: string,
  options?: { page?: number; pageSize?: number }
) {
  const { data: object } = useObject(objectId);
  const { data: fields = [] } = useFieldsByObject(objectId);
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const isInvoiceObject = /^invoice(__c)?$/i.test(String(objectApiName).trim());
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject(objectApiName));
  const usePagination = !(isInvoiceObject && shouldUseGobodo());
  
  return useQuery({
    queryKey: genericListViewKeys.data(objectApiName, listViewId, page, pageSize),
    queryFn: async () => {
      try {
        const listView: ListView = generateDefaultListView(
          objectApiName,
          object?.label || objectApiName,
          fields
        );

        let records: GenericRecord[];
        let totalRecords: number;

        if (isInvoiceObject && shouldUseGobodo()) {
          const invoices = await listAllStripeInvoices();
          records = invoices as GenericRecord[];
          totalRecords = records.length;
        } else if (usePagination && useBackend) {
          const pageResult = await getRecordsForObjectPage(objectApiName, { page, size: pageSize });
          records = pageResult.records;
          totalRecords = pageResult.totalRecords;
        } else if (usePagination) {
          records = await getRecordsForObject(objectApiName);
          totalRecords = records.length;
        } else {
          records = await getRecordsForObject(objectApiName);
          totalRecords = records.length;
        }

        let filteredRecords = records;
        if (listView.filters?.length) {
          filteredRecords = records.filter(record =>
            listView.filters!.every(filter => {
              const recordValue = record[filter.fieldApiName];
              switch (filter.operator) {
                case 'equals': return recordValue === filter.value;
                case 'notEquals': return recordValue !== filter.value;
                case 'contains': return String(recordValue).toLowerCase().includes(String(filter.value).toLowerCase());
                case 'startsWith': return String(recordValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
                case 'greaterThan': return Number(recordValue) > Number(filter.value);
                case 'lessThan': return Number(recordValue) < Number(filter.value);
                default: return true;
              }
            })
          );
        }
        if (listView.sortBy) {
          filteredRecords = [...filteredRecords].sort((a, b) => {
            const aValue = a[listView.sortBy!.fieldApiName];
            const bValue = b[listView.sortBy!.fieldApiName];
            if (aValue === bValue) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;
            const comparison = aValue < bValue ? -1 : 1;
            return listView.sortBy!.direction === 'asc' ? comparison : -comparison;
          });
        }

        if (usePagination && !useBackend) {
          totalRecords = filteredRecords.length;
          const start = page * pageSize;
          filteredRecords = filteredRecords.slice(start, start + pageSize);
        } else if (usePagination && useBackend) {
          filteredRecords = filteredRecords; // already one page
        } else {
          // Invoice branch: no slice
        }

        const listViewRecords = convertToListViewRecords(filteredRecords);
        return { listView, records: listViewRecords, totalRecords } as ListViewData;
      } catch (error) {
        console.error(`[useGenericListViewData] Error fetching list view data for ${objectApiName}:`, error);
        throw error;
      }
    },
    staleTime: 3 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!objectApiName && !!objectId,
    placeholderData: keepPreviousData,
  });
}

/**
 * Create a new record
 */
export function useCreateGenericRecord(objectApiName: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (recordData: Record<string, any>) => {
      return createRecord(objectApiName, recordData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...genericListViewKeys.all, 'data', objectApiName]
      });
      queryClient.invalidateQueries({
        queryKey: genericListViewKeys.byObject(objectApiName)
      });
      queryClient.invalidateQueries({
        queryKey: objectKeys.detail(keyToObjectId(objectApiName))
      });
    }
  });
}

/**
 * Update an existing record
 */
export function useUpdateGenericRecord(objectApiName: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ recordId, updates }: { recordId: string; updates: Record<string, any> }) => {
      return updateRecord(objectApiName, recordId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...genericListViewKeys.all, 'data', objectApiName]
      });
    }
  });
}

/**
 * Delete a record
 */
export function useDeleteGenericRecord(objectApiName: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (recordId: string) => {
      const result = await deleteRecord(objectApiName, recordId);
      if (!result) {
        throw new Error('Failed to delete record');
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...genericListViewKeys.all, 'data', objectApiName]
      });
      queryClient.invalidateQueries({
        queryKey: objectKeys.detail(keyToObjectId(objectApiName))
      });
    }
  });
}

